import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Subject } from "rxjs/Subject";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { locale as translationEnglish } from "./i18n/en";
import { locale as translationRussian } from "./i18n/ru";
import * as _ from "underscore";

import { Observable } from "rxjs/Observable";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";

import { Socket, SocketIoConfig } from "ng-socket-io";

import { AuthModel } from "../models/auth.model";
import { UserModel } from "../models";
import { Roles } from "../acl/roles";
import { defaultPermissions } from "../acl/default-permissions";
import { AclService } from "ng2-acl/dist";

import { config } from "../config";
import { FuseConfigService } from "@fuse/services/config.service";
import * as Navigation from "app/navigation/navigation";
// import { iterateListLike } from '@angular/core/src/change_detection/change_detection_util';

@Injectable()
export class DataService {
  private langField = config.langField;
  private tokenField = config.tokenField;
  private socketTokenField = config.socketTokenField;
  private userField = config.userField;
  private permissionsField = config.permissionsField;
  private companiesField = config.companiesField;
  private gmtListField = config.gmtListField;
  private fieldList = config.fieldList;
  private fieldListLegal = config.fieldListLegal;
  private perPageField = config.perPageField;
  private perPageDefault = config.perPageDefault;
  public role = "";
  private aclData = defaultPermissions;
  private translationObj: any = {};

  private serverUrl = config.serverUrl;
  public serverStorageUrl = config.serverStorageUrl;

  public isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public needUpdateEvents: Subject<boolean> = new Subject();
  public needUpdateTickets: Subject<boolean> = new Subject();
  public ticketAssignedSubj: Subject<any> = new Subject();
  public needUpdateLedgers: Subject<boolean> = new Subject();
  public needUpdateClientLeadHeader: Subject<boolean> = new Subject();
  public needUpdateEmails: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  public updateAvatar: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public userOnline: Subject<boolean> = new Subject();
  public newLeadQueue: BehaviorSubject<any> = new BehaviorSubject(false);
  public updateTicketById: BehaviorSubject<any> = new BehaviorSubject(false);
  public showAlert: BehaviorSubject<{ type: string; message: string }> =
    new BehaviorSubject({ type: "", message: "" });
  public reminder: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public remindersCount: BehaviorSubject<any> = new BehaviorSubject<any>({
    count: 0,
    shake: false,
  });

  private socket: Socket;
  private notification: any = Notification;

  constructor(
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
    private fuseTranslationLoader: FuseTranslationLoaderService,
    private aclService: AclService,
    private configService: FuseConfigService
  ) {
    this.fuseTranslationLoader.loadTranslations(
      translationEnglish,
      translationRussian
    );
    setTimeout(() => {
      this.translate.get("SYSTEM_MESSAGES").subscribe((res) => {
        this.translationObj = res;
      });
    });
    this.wathLogin();
    localStorage.getItem(this.tokenField)
      ? this.isLoggedIn.next(true)
      : this.isLoggedIn.next(false);
    this.detectLocale();
    this.watchOnline();
  }

  wathLogin() {
    this.isLoggedIn.subscribe((loggedIn) => {
      if (!loggedIn && !localStorage.getItem(this.tokenField)) {
        this.socketDisconnect();
        if (
          location.hash.indexOf("#/auth/registration") === -1 &&
          location.hash.indexOf("#/auth/forgot-password") === -1
        ) {
          this.router.navigate(["/auth/login"]);
        }
      } else if (loggedIn && localStorage.getItem(this.tokenField)) {
        this.configService.refreshConfig();
        this.listenSocketEvents();
      }
    });
  }

  watchOnline() {
    this.userOnline.subscribe((value) => {
      // console.log('emitToLeadQueue -> ', value);
      if (value) {
        this.emitToLeadQueue("subscribe");
      } else {
        this.emitToLeadQueue("unsubscribe");
      }
    });
  }

  checkLeadQueueState() {
    return this.http.get(this.serverUrl + "/lead-queue/state");
  }

  login(loginData) {
    this.http.post(this.serverUrl + "/login", loginData).subscribe(
      (res: any) => {
        if (res && !res.errors) {
          // console.log('login -> ', res);
          const authData = new AuthModel(res.data);
          this.storeUserData(authData);
          this.isLoggedIn.next(true);
          this.router.navigate(["/"]);
        } else {
          //  console.error('Auth Error -> ', res);
          if (res.status_code === 403) {
            this.showAlert.next({
              type: "danger",
              message: this.translationObj.WRONG_LOGIN_OR_PWD,
            });
          }
        }
      },
      (err) => {
        if (err.error.status_code === 403) {
          this.showAlert.next({
            type: "danger",
            message: this.translationObj.WRONG_LOGIN_OR_PWD,
          });
        }
      }
    );
  }

  saveFilterUrl(url) {
    localStorage.setItem(config.filterUrl, url);
  }

  getLastFilteredUrl() {
    return localStorage.getItem(config.filterUrl);
  }

  register(registrationData) {
    this.http
      .post(this.serverUrl + "/registration", registrationData)
      .subscribe(
        (res: any) => {
          if (res && !res.errors) {
            const authData = new AuthModel(res.data);
            this.storeUserData(authData);
            this.isLoggedIn.next(true);
            this.router.navigate(["/"]);
          } else {
            if (res.status_code === 403) {
              this.showAlert.next({
                type: "danger",
                message: this.translationObj.WRONG_LOGIN_OR_PWD,
              });
            }
          }
        },
        (err) => {
          if (err.error.status_code === 403) {
            this.showAlert.next({
              type: "danger",
              message: this.translationObj.WRONG_LOGIN_OR_PWD,
            });
          }
        }
      );
  }

  sendEmailForForgotPassword(email) {
    return this.http.post(this.serverUrl + "/password/reset", { email: email });
  }

  changePassword(pin, password, email) {
    this.http
      .post(this.serverUrl + "/password/change", {
        pin: pin,
        password: password,
        email: email,
      })
      .subscribe((res: any) => {
        if (res && !res.errors) {
          const authData = new AuthModel(res.data);
          this.storeUserData(authData);
          this.isLoggedIn.next(true);
          this.router.navigate(["/"]);
        } else {
          if (res.status_code === 403) {
            this.showAlert.next({
              type: "danger",
              message: this.translationObj.WRONG_EMAIL_OR_PIN,
            });
          }
        }
      });
  }

  initAcl(permissions) {
    this.aclData = defaultPermissions;
    const user = this.getProfileData();
    if (user) {
      this.role = new Roles().getRole();
      this.aclData[this.role] = [];
      if (permissions && permissions instanceof Array) {
        permissions.forEach((permission) => {
          this.aclData[this.role].push(permission);
        });
      }
      this.aclService.setAbilities(this.aclData);
      this.aclService.attachRole(this.role);
    }
  }

  listenSocketEvents() {
    const token = this.getSocketToken();
    const lang = localStorage.getItem(this.langField);
    const socketConfig: SocketIoConfig = {
      url: config.soketHost + "?token=" + token + "&lang=" + lang,
      options: {},
    };
    this.socket = new Socket(socketConfig);

    this.socket.on("error", (err) => {
      //  console.error('SOCKET ERORRRRRR !!! -> ', err);
    });
    this.socket.on("connect", () => {
      //  console.log('authenticated');
    });
    this.socket.on("disconnect", () => {
      // console.log('disconnected');
    });

    this.socket.on("newAssignedEvent", (msg) => {
      const onClick = () => {
        let route = "";
        if (msg.data.clientType === "lead") {
          route = msg.data.clientSubtype === "lp" ? "/leads-legal" : "/leads";
        } else if (msg.data.clientType === "client") {
          route =
            msg.data.clientSubtype === "lp" ? "/clients-legal" : "/clients";
        }
        route += "/" + msg.data.clientId + "/events;edit=" + msg.data.eventId;
        this.router.navigateByUrl(route);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.EVENT_TYPE +
          ": " +
          msg.data.type;
      this.showNotification(
        this.translationObj.NEW_ASSIGNED_EVENT,
        message,
        onClick
      );
      this.reminder.next(msg);
      // console.log('newAssignedEvent -> ', msg);
    });

    this.socket.on("newEmail", (msg) => {
      //  console.log('newEmail -> ', msg);
      const onClick = () => {
        this.router.navigate(["/mail"]);
      };
      // this.changeNavItemBadge('mail', 'increace');
      const message = "mail message content must be translated!!!";
      this.showNotification(this.translationObj.NEW_EMAIL, message, onClick);
      this.reminder.next(msg);
    });

    this.socket.on("newDocument", (msg) => {
      const onClick = () => {
        let route = "";
        if (msg.data.clientType === "lead") {
          route = msg.data.clientSubtype === "lp" ? "/leads-legal" : "/leads";
        } else if (msg.data.clientType === "client") {
          route =
            msg.data.clientSubtype === "lp" ? "/clients-legal" : "/clients";
        }
        route += "/" + msg.data.clientId + "/documents";
        this.router.navigateByUrl(route);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.DOC_TYPE +
          ": " +
          msg.data.type;

      this.showNotification(
        this.translationObj.NEW_DOC_ADDED,
        message,
        onClick
      );
      this.reminder.next(msg);
      // console.log('newDocument -> ', msg);
    });

    this.socket.on("newPaymentMethod", (msg) => {
      const onClick = () => {
        let route = "";
        if (msg.data.clientType === "lead") {
          route = msg.data.clientSubtype === "lp" ? "/leads-legal" : "/leads";
        } else if (msg.data.clientType === "client") {
          route =
            msg.data.clientSubtype === "lp" ? "/clients-legal" : "/clients";
        }
        route += "/" + msg.data.clientId + "/details";
        this.router.navigateByUrl(route);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.PAYMENT_METHOD +
          ": " +
          msg.data.type;
      this.showNotification(
        this.translationObj.NEW_PAYMENT_METHOD,
        message,
        onClick
      );
      this.reminder.next(msg);
      // console.log('newPaymentMethod -> ', msg);
    });

    this.socket.on("upcomingEvent", (msg) => {
      // console.log('upcomingEvent -> ', msg);
      const onClick = () => {
        let route = "";
        if (msg.data.clientType === "lead") {
          route = msg.data.clientSubtype === "lp" ? "/leads-legal" : "/leads";
        } else if (msg.data.clientType === "client") {
          route =
            msg.data.clientSubtype === "lp" ? "/clients-legal" : "/clients";
        }
        route += "/" + msg.data.clientId + "/events;edit=" + msg.data.eventId;
        this.router.navigateByUrl(route);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.EVENT_TYPE +
          ": " +
          msg.data.type;
      this.showNotification(
        this.translationObj.NEW_UPCOMING_EVENT,
        message,
        onClick
      );
      this.reminder.next(msg);
    });

    this.socket.on("newTicket", (msg) => {
      const onClick = () => {
        this.router.navigate(["/tickets"]);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.TICKET_SUBJECT +
          ": " +
          msg.data.subject;
      this.showNotification(this.translationObj.NEW_TICKET, message, onClick);
      this.reminder.next(msg);
    });

    this.socket.on("newAssignedTicket", (msg) => {
      const onClick = () => {
        this.router.navigate(["/tickets"]);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.TICKET_SUBJECT +
          ": " +
          msg.data.subject;
      this.showNotification(
        this.translationObj.NEW_ASSIGNED_TICKET,
        message,
        onClick
      );
      this.reminder.next(msg);
    });

    this.socket.on("ticketAssigned", (data) => {
      this.ticketAssignedSubj.next(data);
    });

    this.socket.on("newTicketMessage", (msg) => {
      const onClick = () => {
        this.router.navigate(["/tickets"]);
      };
      const message =
        msg.data.clientType === "client"
          ? this.translationObj.CLIENT
          : this.translationObj.LEAD +
          ": " +
          msg.data.clientName +
          ", " +
          this.translationObj.MESSAGE +
          ": " +
          msg.data.message;
      this.showNotification(
        this.translationObj.NEW_TICKET_MESSAGE,
        message,
        onClick
      );
      this.reminder.next(msg);
      this.updateTicketById.next(msg.data.id);
    });

    const user = new UserModel(this.getProfileData());
    if (user.responsibleFor === "leads") {
      this.socket.on("leadQueue", (msg) => {
        this.newLeadQueue.next(msg);
      });
    }

    this.socket.on("commonMessage", (msg) => {
      this.reminder.next(msg);
    });

    this.socket.on("forceLogout", (msg) => {
      // console.log('forceLogout -> ', msg);
    });
  }

  emitToLeadQueue(cmd) {
    if (this.socket) {
      this.socket.emit("leadQueue", cmd);
    }
  }

  socketDisconnect() {
    //  console.log('socketDisconnect');
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  showNotification(title, message, onClick = null) {
    if (this.notification && this.notification.permission === "granted") {
      const notify = new Notification(title, {
        body: message,
        icon: "/assets/images/logos/logo-color-icon.png",
      });
      if (onClick) {
        notify.onclick = onClick;
      }
    } else if (this.notification && this.notification.permission !== "denied") {
      this.notification.requestPermission((status) => {
        if (status === "granted") {
          const notify = new Notification(title, {
            body: message,
            icon: "/assets/images/logos/logo-color-icon.png",
          });
          if (onClick) {
            notify.onclick = onClick;
          }
        }
      });
    }
  }

  getNotifications(count: number, offset: number) {
    const url = `?count=${count}&offset=${offset}`;
    return this.http.get(this.serverUrl + "/notifications" + encodeURI(url));
  }

  deleteNotification(id) {
    return this.http.post(this.serverUrl + "/notification/destroy", { id: id });
  }

  checkLoggedId() {
    if (this.isLoggedIn.getValue()) {
      this.router.navigate(["/"]);
    }
  }

  logout() {
    this.clearUserData();
    /* this.http.get(this.serverUrl + '/logout').subscribe((res: any) => {
      if (res && !res.errors) {
        this.clearUserData();
      }
    },
    (err: any) => {
      console.error('error -> ', err);
      this.clearUserData();
    }); */
  }

  detectLocale() {
    this.translate.addLangs(config.availableLanguages);
    this.translate.setDefaultLang(config.defaultLocale);

    const lang = localStorage.getItem(this.langField);
    if (lang) {
      this.setLocale(lang);
    } else {
      const browserLanguagePropertyKeys = [
        "languages",
        "language",
        "browserLanguage",
        "userLanguage",
        "systemLanguage",
      ];

      const detectedLocale = _.chain(window.navigator)
        .pick(browserLanguagePropertyKeys)
        .values()
        .flatten()
        .compact()
        .map((x) => {
          return x.substr(0, 2);
        })
        .find((x) => {
          return _.contains(config.availableLanguages, x);
        })
        .value();
      const locale = detectedLocale || config.defaultLocale;
      this.setLocale(locale, true);
    }
  }

  setLocale(lang: string, save = false) {
    this.translate.use(lang);
    if (save) {
      localStorage.setItem(this.langField, lang);
    }
  }

  getCurrentLang() {
    return this.translate.currentLang;
  }

  storeUserData(userdata: AuthModel) {
    localStorage.setItem(this.tokenField, userdata.token);
    localStorage.setItem(this.socketTokenField, userdata.socketToken);
    localStorage.setItem(this.userField, JSON.stringify(userdata.user));
    localStorage.setItem(this.gmtListField, JSON.stringify(userdata.gmtList));
    if (userdata.companies) {
      localStorage.setItem(
        this.companiesField,
        JSON.stringify(userdata.companies)
      );
    }
    this.initAcl(userdata.permissions);
  }

  getProfileData() {
    return JSON.parse(localStorage.getItem(this.userField));
  }

  getUserId() {
    const user = new UserModel(
      JSON.parse(localStorage.getItem(this.userField))
    );
    return user.id;
  }

  refreshProfileData(data) {
    localStorage.setItem(this.userField, JSON.stringify(data));
  }

  getGmtList() {
    return JSON.parse(localStorage.getItem(this.gmtListField));
  }

  getPerPage() {
    return localStorage.getItem(this.perPageField)
      ? localStorage.getItem(this.perPageField)
      : this.perPageDefault;
  }

  setPerPage(countRows) {
    localStorage.setItem(this.perPageField, countRows);
  }

  clearUserData() {
    localStorage.removeItem(this.tokenField);
    localStorage.removeItem(this.socketTokenField);
    localStorage.removeItem(this.userField);
    localStorage.removeItem(this.gmtListField);
    this.aclService.flushRoles();
    localStorage.removeItem(this.permissionsField);
    localStorage.removeItem(this.companiesField);
    this.isLoggedIn.next(false);
  }

  getToken(): string {
    return localStorage.getItem(this.tokenField);
  }

  getSocketToken(): string {
    return localStorage.getItem(this.socketTokenField);
  }

  saveProfileField(fieldName: string, data: any) {
    return this.http.post(this.serverUrl + "/profile/update", {
      field: fieldName,
      data: data,
    });
  }

  saveTableFieldList(data) {
    localStorage.setItem(this.fieldList, data);
  }

  saveTableFieldListLegal(data) {
    localStorage.setItem(this.fieldListLegal, data);
  }

  clearFieldList() {
    localStorage.removeItem(this.fieldList);
  }

  clearFieldListLegal() {
    localStorage.removeItem(this.fieldListLegal);
  }

  getTableFieldList() {
    return localStorage.getItem(this.fieldList);
  }

  getLegalTableFieldList() {
    return localStorage.getItem(this.fieldListLegal);
  }

  getLeads(type, pageNum, perPage, searchForm, sortField, sortDirection) {
    return this.http.post(this.serverUrl + "/" + type + "/lead/list", {
      page: pageNum,
      rowCount: perPage,
      search: searchForm,
      sortField: sortField,
      sortDirection: sortDirection,
    });
  }

  getLeadManagers(type: string) {
    return this.http.get(this.serverUrl + "/" + type + "/managers");
  }

  getClients(type, pageNum, perPage, searchForm, sortField, sortDirection) {
    return this.http.post(this.serverUrl + "/" + type + "/client/list", {
      page: pageNum,
      rowCount: perPage,
      search: searchForm,
      sortField: sortField,
      sortDirection: sortDirection,
    });
  }

  searchLeadGlobal(type, searchField, searchTerm) {
    return this.http.post(this.serverUrl + "/" + type + "/search", {
      field: searchField,
      data: searchTerm,
    });
  }

  searchClientGlobal(searchField, searchTerm) {
    return this.http.post(this.serverUrl + "/clients/search", {
      field: searchField,
      data: searchTerm,
    });
  }

  loadLeadBreadCrumbs(id: string, type: string) {
    return this.http.get(this.serverUrl + "/" + type + "/auxiliary-info/" + id);
  }

  loadDuplicates(id: string, type: string) {
    return this.http.get(
      this.serverUrl + "/" + type + "/" + id + "/duplicates"
    );
  }

  addDuplicate(primaryId, secondaryId) {
    return this.http.post(this.serverUrl + "/duplicate/add", {
      primaryId: primaryId,
      secondaryId: secondaryId,
    });
  }

  searchDuplicate(subtype, searchTerm) {
    return this.http.post(this.serverUrl + "/" + subtype + "/search", {
      data: searchTerm,
    });
  }

  loadClientsBreadCrumbs(id: string) {
    return this.http.get(this.serverUrl + "/client/auxiliary-info/" + id);
  }

  getPartnerInfo(type: string, id: string) {
    return this.http.get(this.serverUrl + "/" + type + "/partnership/" + id);
  }

  updatePartnerInfo(type, id, form) {
    return this.http.post(
      this.serverUrl + "/" + type + "/partnership/update/" + id,
      form
    );
  }

  getPartnershipDashboard(type, id, data) {
    return this.http.get(
      this.serverUrl + "/" + type + "/partnership/charts/" + id,
      { params: data }
    );
  }

  getPartnershipRefferals(type, id, data) {
    return this.http.get(
      this.serverUrl + "/" + type + "/partnership/clients/" + id,
      { params: data }
    );
  }

  getPartnershipWithdrawalHistory(type, id, data) {
    return this.http.get(
      this.serverUrl + "/" + type + "/partnership/withdrawal-history/" + id,
      { params: data }
    );
  }

  getPartnershipRebateHistory(type, id, data) {
    return this.http.get(
      this.serverUrl + "/" + type + "/partnership/rebate-history/" + id,
      { params: data }
    );
  }

  /* FOR DELTE */
  getCountries() {
    return this.http.get(this.serverUrl + "/countries");
  }
  /* FOR DELTE */

  createLead(type) {
    return this.http.get(this.serverUrl + "/" + type + "/create");
  }

  addLead(leadData) {
    return this.http.post(this.serverUrl + "/np/lead/store", leadData);
  }

  addLegalLead(leadData) {
    return this.http.post(this.serverUrl + "/lp/lead/store", leadData);
  }

  /* FOR DELETE */
  getLead(id: number) {
    return this.http.get(this.serverUrl + "/lead/edit/" + id);
  }
  /* FOR DELETE */

  getClient(id: number, type: string) {
    return this.http.get(this.serverUrl + "/" + type + "/edit/" + id);
  }

  attachOrgActivity(type, id, clientId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/attach/orgactivities/" + clientId,
      { id: id }
    );
  }

  detachOrgActivity(type, id, clientId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/detach/orgactivities/" + clientId,
      { id: id }
    );
  }

  saveLeadField(type, fieldData, leadId) {
    return this.http.post(
      this.serverUrl +
      "/" +
      type +
      "/update/" +
      fieldData.fieldBlock.toLowerCase() +
      "/" +
      leadId,
      { field: fieldData.fieldName, data: fieldData.fieldValue }
    );
  }

  saveClientField(fieldData, clientId) {
    return this.http.post(
      this.serverUrl +
      "/client/update/" +
      fieldData.fieldBlock.toLowerCase() +
      "/" +
      clientId,
      { field: fieldData.fieldName, data: fieldData.fieldValue }
    );
  }

  saveDocumentsfield(type, docId, fieldData, leadId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/document/update/" + leadId,
      { id: docId, field: fieldData.fieldName, data: fieldData.fieldValue }
    );
  }

  addPaymentMethod(data, type, clientId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/payment-method/store/" + clientId,
      data
    );
  }

  updatePaymentMethod(type, clientId, data) {
    return this.http.post(
      this.serverUrl + "/" + type + "/payment-method/update/" + clientId,
      data
    );
  }

  deletePaymentMethod(type, id, clientId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/payment-method/destroy/" + clientId,
      { id: id }
    );
  }

  addAdditionalContact(data, type) {
    return this.http.post(
      this.serverUrl + "/" + type + "/additional-contact/store",
      data
    );
  }

  deleteAdditionalContact(id, clientId, type) {
    return this.http.post(
      this.serverUrl + "/" + type + "/additional-contact/destroy",
      { id: id, clientId: clientId }
    );
  }

  addRepresentative(type, clientId, formData) {
    return this.http.post(
      this.serverUrl + "/" + type + "/representative/store/" + clientId,
      formData
    );
  }

  addLegalRepresentative(type, clientId, formData, formId, typeId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/representative/store/" + clientId,
      { formId: formId, typeId: typeId, data: formData }
    );
  }

  updateRepresentative(type, clientId, formData) {
    return this.http.post(
      this.serverUrl + "/" + type + "/representative/update/" + clientId,
      formData
    );
  }

  updateLegalRepresentative(type, clientId, formData, id, formId, typeId) {
    return this.http.post(
      this.serverUrl + "/" + type + "/representative/update/" + clientId,
      { id: id, formId: formId, typeId: typeId, data: formData }
    );
  }

  deleteRepresentative(type, clientId, id) {
    return this.http.post(
      this.serverUrl + "/" + type + "/representative/destroy/" + clientId,
      { id: id }
    );
  }

  updateAgreementNo(type, data, id) {
    return this.http.post(
      this.serverUrl + "/" + type + "/update/agreement-no/" + id,
      data
    );
  }

  generateSoato(type, formData) {
    return this.http.post(this.serverUrl + "/soato/" + type, formData);
  }

  loadJson(url: string): Observable<any> {
    return this.http
      .get(url)
      .map((res: any) => res)
      .catch((error: any) => error);
  }

  getDocuments(type: string, userId: string) {
    return this.http.get(this.serverUrl + "/" + type + "/documents/" + userId);
  }

  addDocument(type, clientId, formData) {
    return this.http.post(
      this.serverUrl + "/" + type + "/document/store/" + clientId,
      formData
    );
  }

  getPDF(url) {
    return this.http.get(url, { responseType: "arraybuffer" });
  }

  getComments(type: string, userId: string, pageNum: string) {
    const url = `?page=${pageNum}`;
    return this.http.get(
      this.serverUrl + "/" + type + "/comments/" + userId + encodeURI(url)
    );
  }

  addComment(type, formData) {
    return this.http.post(
      this.serverUrl + "/" + type + "/comment/store",
      formData
    );
  }

  getActivities(type: string, userId: string, pageNum: string) {
    const url = `?page=${pageNum}`;
    return this.http.get(
      this.serverUrl + "/" + type + "/activities/" + userId + encodeURI(url)
    );
  }

  getEvents(
    type,
    clientId: string,
    pageNum: string,
    dateFrom,
    dateTo,
    statusId,
    typeId
  ) {
    const url = `?page=${pageNum}&dateFrom=${dateFrom}&dateTo=${dateTo}&statusId=${statusId}&typeId=${typeId}`;
    return this.http.get(
      this.serverUrl + "/" + type + "/events/" + clientId + encodeURI(url)
    );
  }

  getEvent(id) {
    return this.http.get(this.serverUrl + "/event/" + id);
  }

  getTickets(
    type,
    clientId: string,
    pageNum: string,
    dateFrom,
    dateTo,
    statusId,
    typeId
  ) {
    const url = `?page=${pageNum}&dateFrom=${dateFrom}&dateTo=${dateTo}&statusId=${statusId}&typeId=${typeId}`;
    return this.http.get(
      this.serverUrl + "/" + type + "/tickets/" + clientId + encodeURI(url)
    );
  }

  getTicketById(id) {
    return this.http.get(this.serverUrl + "/ticket/show/" + id);
  }

  updateTicket(ticketId, data) {
    return this.http.post(this.serverUrl + "/ticket/update/" + ticketId, data);
  }

  sendTicketMessage(message: string, id: number) {
    return this.http.post(this.serverUrl + "/ticket/message/store", {
      ticketId: id,
      message: message,
    });
  }

  addTicket(type, userId, data) {
    return this.http.post(
      this.serverUrl + "/" + type + "/ticket/store/" + userId,
      data
    );
  }

  getTicketsSubsidiaryData() {
    return this.http.get(this.serverUrl + "/tickets/utility");
  }

  loadCalendarEvents(view, year, month, day, week) {
    const url = `?view=${view}&year=${year}&month=${month}&day=${day}&week=${week}`;
    return this.http.get(this.serverUrl + "/events" + encodeURI(url));
  }

  updateEventDate(id, date) {
    return this.http.post(this.serverUrl + "/event/update-date/" + id, {
      date: date,
    });
  }

  getLeadSummary(type: string, userId: string) {
    return this.http.get(this.serverUrl + "/" + type + "/summary/" + userId);
  }

  getClientSummary(userId: string) {
    return this.http.get(this.serverUrl + "/client/summary/" + userId);
  }

  getEventSubsidiaryData() {
    return this.http.get(this.serverUrl + "/event/add");
  }

  getTicketSubsidiaryData() {
    return this.http.get(this.serverUrl + "/ticket/create");
  }

  addEvent(type, userId, data) {
    return this.http.post(
      this.serverUrl + "/" + type + "/event/store/" + userId,
      data
    );
  }

  updateEvent(type, userId, data) {
    return this.http.post(
      this.serverUrl + "/" + type + "/event/update/" + userId,
      data
    );
  }

  getSalesManagers() {
    return this.http.get(this.serverUrl + "/sales-managers");
  }

  getAccountManagers() {
    return this.http.get(this.serverUrl + "/account-managers");
  }

  massAssign(type, ids, id) {
    return this.http.post(this.serverUrl + "/" + type + "/mass-assign", {
      ids: ids,
      id: id,
    });
  }

  /* FOR DELETE */
  massClientAssign(ids, id) {
    return this.http.post(this.serverUrl + "/clients/update", {
      ids: ids,
      id: id,
    });
  }
  /* FOR DELETE */

  getEmailHistory(id, pageNum) {
    const url = `?page=${pageNum}`;
    return this.http.get(this.serverUrl + "/emails/" + id + encodeURI(url));
  }

  getEmployees() {
    return this.http.get(this.serverUrl + "/employees");
  }

  addEmployee(data) {
    return this.http.post(this.serverUrl + "/employee/store", data);
  }

  updateEmployee(id, data) {
    return this.http.post(this.serverUrl + "/employee/update/" + id, data);
  }

  deleteEmployee(id) {
    return this.http.post(this.serverUrl + "/employee/destroy", { id: id });
  }

  saveEmployeeHierarchy(users) {
    return this.http.post(this.serverUrl + "/employees/hierarchy/update", {
      users: users,
    });
  }

  appointEmployeeAsHead(id) {
    return this.http.post(this.serverUrl + "/employee/appoint-as-head", {
      id: id,
    });
  }

  getDeletedEmployees(count, offset) {
    const url = `?count=${count}&offset=${offset}`;
    return this.http.get(
      this.serverUrl + "/employees/archive" + encodeURI(url)
    );
  }

  restoreEmployee(id) {
    return this.http.post(this.serverUrl + "/employee/restore", { id: id });
  }

  getEmployeesList() {
    return this.http.get(this.serverUrl + "/employees/list");
  }

  loadUserPermissions(userId) {
    return this.http.get(this.serverUrl + "/permissions/of-user/" + userId);
  }

  saveUserPermissions(userId, permissions) {
    return this.http.post(this.serverUrl + "/permissions/update", {
      userId: userId,
      permissions: permissions,
    });
  }

  getPermissionsOfDepartment(departmentId) {
    return this.http.get(
      this.serverUrl + "/permissions/of-department/" + departmentId
    );
  }

  getDataForAddUser() {
    return this.http.get(this.serverUrl + "/employee/create");
  }

  getCompanyUsers(id = "") {
    const url = id ? "/address-book/" + id : "/address-book";
    return this.http.get(this.serverUrl + url);
  }

  getTradingAccountInfo(clientId, accountId, platform, type, clientType) {
    const url =
      "?clientId=" +
      clientId +
      "&accountId=" +
      accountId +
      "&platform=" +
      platform +
      "&type=" +
      type;
    return this.http.get(
      this.serverUrl +
      "/" +
      clientType +
      "/trading-account/info" +
      encodeURI(url)
    );
  }

  getTradingAccountLedgers(
    clientId,
    accountId,
    platform,
    type,
    pageNum,
    clientType
  ) {
    const url =
      "?clientId=" +
      clientId +
      "&accountId=" +
      accountId +
      "&platform=" +
      platform +
      "&type=" +
      type +
      "&page=" +
      pageNum;
    return this.http.get(
      this.serverUrl +
      "/" +
      clientType +
      "/trading-account/ledgers" +
      encodeURI(url)
    );
  }

  getTradingAccountClosedTrades(
    clientId,
    accountId,
    platform,
    type,
    pageNum,
    clientType
  ) {
    const url =
      "?clientId=" +
      clientId +
      "&accountId=" +
      accountId +
      "&platform=" +
      platform +
      "&type=" +
      type +
      "&page=" +
      pageNum;
    return this.http.get(
      this.serverUrl +
      "/" +
      clientType +
      "/trading-account/closed-trades" +
      encodeURI(url)
    );
  }

  depositToAccount(data) {
    return this.http.post(this.serverUrl + "/trading-account/deposit", data);
  }

  getDashboardData(from, to, userId, currencyId, diagrams) {
    let url = "?";
    url += userId ? "&userId=" + userId : "";
    url += currencyId ? `&currency=${currencyId}` : "";
    url += from ? "&from=" + from : "";
    url += to ? "&to=" + to : "";
    url += diagrams ? "&diagrams=" + JSON.stringify(diagrams) : "";
    return this.http.get(this.serverUrl + "/dashboard" + url);
  }

  getSubordinates() {
    return this.http.get(this.serverUrl + "/subordinates");
  }

  getReportTypes() {
    return this.http.get(this.serverUrl + "/report/types");
  }

  downloadReport(formData) {
    return this.http.post(this.serverUrl + "/report/export", formData, {
      responseType: "blob",
    });
  }

  redirectToDashboard(message) {
    this.showAlert.next({ type: "danger", message: message });
    this.router.navigate(["/"]);
  }

  getDepartments() {
    return this.http.get(this.serverUrl + "/departments");
  }

  addDepartment(department) {
    return this.http.post(this.serverUrl + "/department/store", {
      name: department.departmentName,
      external: Boolean(department.external),
    });
  }

  updateDepartment(department) {
    return this.http.post(
      this.serverUrl + "/department/update/" + department.id,
      { name: department.name, external: department.external }
    );
  }

  deleteDepartment(id) {
    return this.http.post(this.serverUrl + "/department/destroy", { id: id });
  }

  getRoles() {
    return this.http.get(this.serverUrl + "/roles");
  }

  getRole(id) {
    return this.http.get(this.serverUrl + "/role/edit/" + id);
  }

  createRole() {
    return this.http.get(this.serverUrl + "/role/create");
  }

  addRole(data) {
    return this.http.post(this.serverUrl + "/role/store", data);
  }

  updateRole(id, data) {
    return this.http.post(this.serverUrl + "/role/update/" + id, data);
  }

  deleteRole(id) {
    return this.http.post(this.serverUrl + "/role/destroy", { id: id });
  }

  getAllTickets(count, offset, searchStr = "") {
    const search = searchStr ? "&searchStr=" + encodeURI(searchStr) : "";
    const urlPart = "?count=" + count + "&offset=" + offset + search;
    return this.http.get(this.serverUrl + "/tickets" + urlPart);
  }

  getTicketsByStatus(statusId, count, offset, searchStr = "") {
    const search = searchStr ? "&searchStr=" + encodeURI(searchStr) : "";
    const urlPart = "?count=" + count + "&offset=" + offset + search;
    return this.http.get(
      this.serverUrl + "/tickets/status/" + statusId + urlPart
    );
  }

  getTicketsByType(typeId, count, offset, searchStr = "") {
    const search = searchStr ? "&searchStr=" + encodeURI(searchStr) : "";
    const urlPart = "?count=" + count + "&offset=" + offset + search;
    return this.http.get(this.serverUrl + "/tickets/type/" + typeId + urlPart);
  }

  getOrderToWithdraw(ticketId) {
    return this.http.get(
      this.serverUrl + "/tickets/withdraw-order/" + ticketId
    );
  }

  getMailSettings() {
    return this.http.get(this.serverUrl + "/mail-settings");
  }

  getMoveFolders() {
    return this.http.get(this.serverUrl + "/mailbox/move-folders");
  }

  saveMailSettings(form) {
    return this.http.post(this.serverUrl + "/mail-settings/update", form);
  }

  testImapConnection() {
    return this.http.get(this.serverUrl + "/mailbox/imap/test");
  }

  getMailboxFolders() {
    return this.http.get(this.serverUrl + "/mailbox/folders");
  }

  getEmailList(folderId, searchStr = "", offset = 0) {
    let urlPart = "";
    if (searchStr.length > 0) {
      urlPart = "?searchStr=" + encodeURI(searchStr);
    }
    if (offset !== 0) {
      if (searchStr.length > 0) {
        urlPart += "&offset=" + offset;
      } else {
        urlPart += "?offset=" + offset;
      }
    }
    return this.http.get(
      this.serverUrl + "/mailbox/folder/" + folderId + urlPart
    );
  }

  getEmailByFilter(type, searchStr = "", offset = 0) {
    let urlPart = "";
    if (searchStr.length > 0) {
      urlPart = "?searchStr=" + encodeURI(searchStr);
    }
    if (offset !== 0) {
      if (searchStr.length > 0) {
        urlPart += "&offset=" + offset;
      } else {
        urlPart += "?offset=" + offset;
      }
    }
    return this.http.get(
      this.serverUrl + "/mailbox/" + type.toLowerCase() + urlPart
    );
  }

  markMails(ids, type) {
    return this.http.post(this.serverUrl + "/mailbox/mark", {
      ids: ids,
      type: type,
    });
  }

  deleteMails(ids) {
    return this.http.post(this.serverUrl + "/mailbox/destroy", { ids: ids });
  }

  moveMailsToFolder(ids, folderId) {
    return this.http.post(this.serverUrl + "/mailbox/move", {
      ids: ids,
      folderId: folderId,
    });
  }

  getAttachment(id) {
    return this.http.get(this.serverUrl + "/mailbox/attachment/" + id);
  }

  getMail(id) {
    return this.http.get(this.serverUrl + "/mailbox/message/" + id);
  }

  sendEmail(formdata) {
    return this.http.post(this.serverUrl + "/mailbox/send", formdata);
  }

  moveToDrafts(formdata) {
    return this.http.post(this.serverUrl + "/mailbox/to-drafts", formdata);
  }

  changeNavItemBadge(type, itemId) {
    const item = this.findNavItem(itemId);
    if (type === "increace") {
      item.badge.title++;
    } else if (type === "decreace" && item) {
      if (item.badge.title >= 0) {
        item.badge.title--;
      }
    }
  }

  findNavItem(id) {
    const nav = Navigation.navigation;
    const items = [];
    iterateNav(nav);

    function iterateNav(arr) {
      for (let i = 0; arr.length > i; i++) {
        if (arr[i].children) {
          iterateNav(arr[i].children);
        } else {
          items.push(arr[i]);
        }
      }
    }
    const find = items.find((i) => i.id === id);
    return find ? find : null;
  }

  importLeads(file) {
    return this.http.post(this.serverUrl + "/contacts/import", file);
  }

  exportLeads(form) {
    return this.http.post(this.serverStorageUrl + "/contacts/export", form);
  }

  getPaymentRates() {
    return this.http.get(this.serverUrl + "/partnership/fees");
  }

  exportPaymentRates(type) {
    return this.http.get(this.serverUrl + "/partnership/fees/" + type);
  }

  importPaymentRates(type, data) {
    return this.http.post(this.serverUrl + "/partnership/fees/update", data);
    // return this.http.post(this.serverUrl + '/partnership/fees/' + type + '/update', file);
  }

  // EMAILS

  loadSelections(page, perPage) {
    const url = `?page=${page}&perPage=${perPage}`;
    return this.http.get(
      this.serverUrl + "/bulk-emails/queries" + encodeURI(url)
    );
  }

  loadSelectionRules() {
    return this.http.get(this.serverUrl + "/bulk-emails/query/rules");
  }

  saveUserSelection(data) {
    return this.http.post(this.serverUrl + "/bulk-emails/query/store", data);
  }

  updateUserSelection(data) {
    return this.http.post(this.serverUrl + "/bulk-emails/query/update", data);
  }

  checkSelection(data) {
    return this.http.post(this.serverUrl + "/bulk-emails/query/check", data);
  }

  loadSelection(id) {
    return this.http.get(this.serverUrl + "/bulk-emails/query/" + id);
  }

  deleteUserSelection(id) {
    return this.http.post(this.serverUrl + "/bulk-emails/query/destroy", {
      id: id,
    });
  }

  getEmailTemplatesImagesUploadPath(): string {
    return `${this.serverUrl}/bulk-emails/template/image/store`;
  }

  getEmailTemplatesImages(): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-emails/template/images`);
  }

  uploadEmailTemplatesImages(formData: any): Observable<any> {
    return this.http.post(
      `${this.serverUrl}/bulk-emails/template/image/store`,
      formData
    );
  }

  getEmailTemplates(page, perPage): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-emails/templates`, {
      params: { page, perPage },
    });
  }

  getEmailTemplateById(id: string): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-emails/template/${id}`);
  }

  saveEmailTemplate(
    html: string,
    name: string,
    description: string
  ): Observable<any> {
    return this.http.post(`${this.serverUrl}/bulk-emails/template/store`, {
      html,
      name,
      description,
    });
  }

  updateEmailTemplate(
    id: string,
    html: string,
    name: string,
    description: string
  ): Observable<any> {
    return this.http.post(`${this.serverUrl}/bulk-emails/template/update`, {
      id,
      html,
      name,
      description,
    });
  }

  deleteEmailTemplate(id: string): Observable<any> {
    return this.http.post(`${this.serverUrl}/bulk-emails/template/destroy`, {
      id,
    });
  }

  getEmailTemplatesVariables(): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-emails/template/vars`);
  }

  loadBulkEmails(page, perPage): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-emails`, {
      params: { page, perPage },
    });
  }

  getBulkEmail(id): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-email/${id}`);
  }

  startBulkEmail(data): Observable<any> {
    return this.http.post(`${this.serverUrl}/bulk-email/send`, data);
  }

  getBulkUtilityLists(): Observable<any> {
    return this.http.get(`${this.serverUrl}/bulk-email/utility-lists`);
  }

  // Copytrading

  getAccountListForCreateProvider() {
    return this.http.get(this.serverUrl + "/cp/providers/create");
  }

  getCPSubscriptions() {
    return this.http.get(this.serverUrl + "/cp/subscriptions");
  }

  getCPProviders(page = 1, perPage = 20) {
    return this.http.get(this.serverUrl + "/cp/providers", {
      params: { page: String(page), perPage: String(perPage) },
    });
  }

  getMyCPProviders() {
    return this.http.get(this.serverUrl + "/cp/providers/my");
  }

  subscribeToCPProvider(data) {
    return this.http.post(this.serverUrl + "/cp/subscribe", data);
  }

  createProvider(clientId, data) {
    return this.http.post(
      this.serverUrl + `/cp/clients/${clientId}/providers`,
      data
    );
  }

  updateProvider(clientId, data) {
    return this.http.post(
      this.serverUrl + `/cp/clients/${clientId}/provider/update`,
      data
    );
  }

  getCPProviderPayouts(providerId, offset = 0, limit = 20) {
    return this.http.get(
      this.serverUrl + `/cp/providers/${providerId}/payouts`,
      { params: { offset: String(offset), limit: String(limit) } }
    );
  }

  getCPProviderSubscribers(providerId) {
    return this.http.get(
      this.serverUrl + `/cp/providers/${providerId}/subscribers`
    );
  }

  getCPProviderInfo(providerId) {
    return this.http.get(this.serverUrl + `/cp/providers/${providerId}`);
  }

  deleteProvider(providerId) {
    return this.http.post(
      this.serverUrl + `/cp/providers/${providerId}/destroy`,
      {}
    );
  }

  getCPSubscriptionsPayouts(subscriberId, offset = 0, limit = 20) {
    return this.http.get(
      this.serverUrl + `/cp/subscriptions/${subscriberId}/payouts`,
      { params: { offset: String(offset), limit: String(limit) } }
    );
  }

  getCPSubscriptionInfo(subscriberId) {
    return this.http.get(this.serverUrl + `/cp/subscriptions/${subscriberId}`);
  }

  // Copytrading

  getAdminCommands() {
    return this.http.get(`${this.serverUrl}/admin-actions`);
  }

  execAdminCommand(command) {
    return this.http.post(`${this.serverUrl}/admin-action/exec`, { command });
  }

  downloadClientCard(type: 'np' | 'lp', subType: 'client' | 'lead', clientId: string) {
    return this.http.get(`${this.serverUrl}/${type.toLowerCase()}/${subType.toLowerCase()}/client-card/${clientId}`, { responseType: 'blob' });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/data.service.ts